import * as S from './styles.ts';
import { AnimatePresence, motion } from 'framer-motion';
import { useTypedDispatch, useTypedSelector } from '@/redux/helpers/hooks.ts';
import { deleteCartItem } from '@/redux/slices/user/thunks/deleteCartItem.ts';
import { useRouter } from '@/lib/helpers/hooks/useRouter.ts';
import useCurrency from '@/lib/helpers/hooks/useCurrency.ts';
import { TCartItem } from '@/redux/slices/user/types.ts';
import {
  Button,
  Checkbox,
  IncrementButton,
  Input,
  LoadingSpinner,
  P,
  Popover,
  Select,
} from '@template/components';
import { updateCartItem } from '@/redux/slices/user/thunks/updateCartItem.ts';
import { getCart } from '@/redux/slices/user/thunks/getCart.ts';
import { useEffect, useState } from 'react';
import useDebouncedValue from '@/lib/helpers/hooks/useDebouncedValue.ts';
import moment from 'moment';
import { CircleHelp } from 'lucide-react';
import Trash2 from '@template/icons/Trash2.tsx';

interface ICartDropdownProps {
  footer?: any;
  advanced?: boolean;
}

interface ICartItemProps {
  item: TCartItem;
}

const DomainTerm = ({ item }: Partial<ICartItemProps>) => {
  const dispatch = useTypedDispatch();

  const selectedTerm = item.period;

  return (
    <div
      style={{
        gridColumn: '1 / span 2',
      }}
    >
      <Select
        label='Term'
        selected={selectedTerm}
        loading={item.loading}
        disabled={item.loading}
        onChange={(val) =>
          dispatch(
            updateCartItem({
              id: item.id,
              slug: `/svc/cart/item/${item.id}`,
              data: {
                action: 'change_period',
                period: val,
              },
            })
          ).then(() => dispatch(getCart()))
        }
        fullWidth
        options={[
          {
            label: '1 Year',
            key: 1,
          },
          {
            label: '2 Years',
            key: 2,
          },
          {
            label: '3 Years',
            key: 3,
          },
          {
            label: '4 Years',
            key: 4,
          },
          {
            label: '5 Years',
            key: 5,
          },
          {
            label: '6 Years',
            key: 6,
          },
          {
            label: '7 Years',
            key: 7,
          },
          {
            label: '8 Years',
            key: 8,
          },
          {
            label: '9 Years',
            key: 9,
          },
          {
            label: '10 Years',
            key: 10,
          },
        ]}
        popupProps={{
          width: 200,
          placement: 'bottom-end',
        }}
      />
    </div>
  );
};

const DomainNameServer = ({ item }: Partial<ICartItemProps>) => {
  const dispatch = useTypedDispatch();
  const customNameservers = useTypedSelector(
    (state) => state.user.cart.ns_groups
  );
  const keys = Object.keys(customNameservers);

  const nameServerUuid = item.extra_details.nameserver_group_uuid;

  const keepNameServers = item.extra_details.keep_nameservers;

  const options = [
    {
      label: 'Yay.com NS',
      key: 'yay',
    },
  ];
  keys.forEach((key) => {
    options.push({
      label: customNameservers[key],
      key: key,
    });
  });

  return (
    <>
      <div
        style={{
          gridColumn: '1 / span 2',
        }}
      >
        <Select
          fullWidth
          disabled={keepNameServers || item.loading}
          loading={item.loading}
          selected={keepNameServers ? 'yay' : nameServerUuid}
          onChange={(val) => {
            dispatch(
              updateCartItem({
                id: item.id,
                slug: `/svc/cart/domain-transfer/${item.id}`,
                data: {
                  nameserver_group_uuid: val,
                },
              })
            ).then(() => dispatch(getCart()));
          }}
          options={options}
          popupProps={{
            width: 200,
            placement: 'bottom-end',
          }}
        />
      </div>
      <Checkbox
        checked={keepNameServers}
        label='Keep Name Servers'
        blob={{
          size: 'md',
        }}
        onChange={(val) => {
          if (item.loading) return;
          dispatch(
            updateCartItem({
              id: item.id,
              slug: `/svc/cart/domain-transfer/${item.id}`,
              data: {
                nameserver_group_uuid: val ? null : 'yay',
              },
            })
          ).then(() => dispatch(getCart()));
        }}
      />
    </>
  );
};

const DomainContactType = ({ item }: Partial<ICartItemProps>) => {
  const dispatch = useTypedDispatch();
  const standardInputDetails = {
    placeholder: 'Registered Company Number',
  };

  const itemCompanyNumber = item.extra_details.uk_company_number;
  const itemCompanyType = item.extra_details.uk_company_type;

  const [companyNumber, setCompanyNumber] = useState(itemCompanyNumber);
  const [companyType, setCompanyType] = useState(itemCompanyType);
  const [mounted, setMounted] = useState(false);
  const debouncedCompanyNumber = useDebouncedValue(companyNumber, 500);

  const options = [
    {
      key: 'LTD',
      label: 'UK Registered Limited Company',
      inputDetails: standardInputDetails,
    },
    {
      key: 'PLC',
      label: 'UK Public Limited Company',
      inputDetails: standardInputDetails,
    },
    {
      key: 'PTNR',
      label: 'UK Partnership',
    },
    {
      key: 'STRA',
      label: 'UK Sole Trader',
    },
    {
      key: 'LLP',
      label: 'UK Limited Liability Partnership',
      inputDetails: standardInputDetails,
    },
    {
      key: 'IP',
      label: 'UK Industrial/Provident Registered Company',
      inputDetails: standardInputDetails,
    },
    {
      key: 'IND',
      label: 'UK Individual (representing self)',
    },
    {
      key: 'SCH',
      label: 'UK School',
      inputDetails: {
        placeholder: 'Registered School Number',
      },
    },
    {
      key: 'RCHAR',
      label: 'UK Registered Charity',
      inputDetails: {
        placeholder: 'Registered Charity Number',
      },
    },
    {
      key: 'GOV',
      label: 'UK Government Body',
    },
    {
      key: 'CRC',
      label: 'UK Corporation by Royal Charter',
    },
    {
      key: 'STAT',
      label: 'UK Statutory Body',
    },
    {
      key: 'OTHER',
      label:
        'UK Entity that does not fit into any of the above (e.g. clubs, associations, many universities)',
    },
    {
      key: 'FIND',
      label: 'Non-UK Individual (representing self)',
    },
    {
      key: 'FCORP',
      label: 'Non-UK Corporation',
    },
    {
      key: 'FOTHER',
      label:
        'Non-UK Entity that does not fit into any of the above (e.g. charities, schools, clubs, associations)',
    },
  ];

  const selectedOptionKey = item.extra_details.uk_company_type;
  const selectedOptionDetails = options.find(
    (option) => option.key === selectedOptionKey
  );

  useEffect(() => {
    if (mounted) {
      dispatch(
        updateCartItem({
          id: item.id,
          slug: `/svc/cart/domain-details/${item.id}`,
          data: {
            extra_details: {
              uk_company_type: companyType,
              uk_company_number: debouncedCompanyNumber,
            },
          },
        })
      ).then(() => dispatch(getCart()));
    } else {
      setMounted(true);
    }
  }, [debouncedCompanyNumber, companyType]);

  return (
    <>
      <Select
        placeholder='Select An Option'
        label='Contact Type'
        required
        fullWidth
        loading={item.loading}
        disabled={item.loading}
        selected={selectedOptionKey}
        options={options}
        onChange={setCompanyType}
        popupProps={{
          width: 200,
          placement: 'bottom-end',
        }}
      />
      {selectedOptionDetails?.inputDetails && (
        <Input
          label='Register Company Number'
          required
          fullWidth
          value={companyNumber}
          loading={item.loading}
          disabled={item.loading}
          onChange={setCompanyNumber}
          {...selectedOptionDetails.inputDetails}
        />
      )}
    </>
  );
};

const UsDomainContactType = ({ item }: Partial<ICartItemProps>) => {
  const dispatch = useTypedDispatch();

  const selectOptions = [
    {
      key: 'P1',
      label: 'Business use for profit',
    },
    {
      key: 'P2',
      label: 'Non-profit business, club, association, religious organisation',
    },
    {
      key: 'P3',
      label: 'Personal use',
    },
    {
      key: 'P4',
      label: 'Educational purpose',
    },
    {
      key: 'P5',
      label: 'Government purpose',
    },
  ];
  const personOptions = [
    {
      label:
        'A natural person (i) who is a United States citizen, (ii) who is a permanent resident of the United States of America or any of its possessions or territories, or (iii) whose primary place of domicile is in the United States of America or any of its possessions.',
      key: 'C11',
    },
    {
      label:
        'A United States entity or organization that is (i) incorporated within one of the fifty (50) U.S. states, the District of Columbia, or any of the United States possessions or territories, or (ii) organized or otherwise constituted under the laws of a state of the United States of America, the District of Columbia or any of its possessions or territories (including a federal, state, or local government of the United States or a political subdivision thereof, and non-commercial organizations based in the United States)',
      key: 'C21',
    },
    {
      label:
        'A foreign entity or organization that has a bona fide presence in the United States of America or any of its possessions or territories.\n' +
        '\n' +
        'By choosing this option I certify that I have a “bona fide presence in the United States” on the basis of real and substantial lawful connections with, or lawful activities in, the United States of America.',
      key: 'C31',
    },
  ];

  const itemPurpose = item.extra_details.us_app_purpose;
  const itemNexusCategory = item.extra_details.us_nexus_category;

  return (
    <>
      <Select
        fullWidth
        disabled={item.loading}
        loading={item.loading}
        label='Purpose'
        selected={itemPurpose}
        options={selectOptions}
        placeholder='Select An Option'
        onChange={(val) =>
          dispatch(
            updateCartItem({
              id: item.id,
              slug: `/svc/cart/domain-details/${item.id}`,
              data: {
                extra_details: {
                  us_app_purpose: val,
                  us_nexus_category: itemNexusCategory,
                },
              },
            })
          ).then(() => dispatch(getCart()))
        }
        popupProps={{
          width: 200,
          placement: 'bottom-end',
        }}
      />
      <Select
        options={personOptions}
        fullWidth
        selected={itemNexusCategory}
        disabled={item.loading}
        loading={item.loading}
        label='Nexus Category'
        placeholder='Select An Option'
        onChange={(val) =>
          dispatch(
            updateCartItem({
              id: item.id,
              slug: `/svc/cart/domain-details/${item.id}`,
              data: {
                extra_details: {
                  us_app_purpose: itemPurpose,
                  us_nexus_category: val,
                },
              },
            })
          ).then(() => dispatch(getCart()))
        }
        popupProps={{
          width: 200,
          placement: 'bottom-end',
        }}
      />
    </>
  );
};

const VoipUserCount = ({ item }: Partial<ICartItemProps>) => {
  const dispatch = useTypedDispatch();
  const quantity = item.period;
  const [userCount, setUserCount] = useState(quantity);
  const debouncedUserCount = useDebouncedValue(userCount, 500);
  const isPositive = (val: number | string) => {
    const numericValue = parseInt(val as string);
    return !isNaN(numericValue) && numericValue > 0;
  };

  useEffect(() => {
    if (debouncedUserCount !== quantity && isPositive(debouncedUserCount)) {
      dispatch(
        updateCartItem({
          id: item.id,
          slug: `/svc/cart/item/${item.id}`,
          data: {
            action: 'change_period',
            period: debouncedUserCount,
          },
        })
      ).then(() => dispatch(getCart()));
    }
  }, [debouncedUserCount]);

  const handleSetUserCount = (val: number | string) => {
    const min = 1;
    setUserCount(Math.max(min, Number(val)));
  };

  return (
    <>
      <IncrementButton
        type='number'
        value={userCount}
        setValue={handleSetUserCount}
        style={{
          marginLeft: 'auto',
        }}
      />
    </>
  );
};

const Extras = ({ item }: Partial<ICartItemProps>) => {
  const isDomain = item.type.name === 'Domain Name';
  const isDomainTransfer = isDomain && item.type.action === 'transfer';
  const isDomainBackorder = isDomain && item.type.action === 'backorder';
  const isDomainPurchase = isDomain && !isDomainTransfer && !isDomainBackorder;
  const isVoipUser = item.type.name === 'User';

  if (!isDomainTransfer && !isDomainPurchase && !isVoipUser) {
    return;
  }

  return (
    <S.ExtrasContainer>
      {isDomainPurchase ? (
        <>
          <DomainTerm item={item} />
          {item.requires_extra_details && (
            <>
              {item.type.variation === 'us' ? (
                <UsDomainContactType item={item} />
              ) : (
                <DomainContactType item={item} />
              )}
            </>
          )}
        </>
      ) : isDomainTransfer ? (
        <DomainNameServer item={item} />
      ) : null}
    </S.ExtrasContainer>
  );
};

export const CartItem = ({ item }: ICartItemProps) => {
  const dispatch = useTypedDispatch();
  const currency = useCurrency();

  const { loading } = item;

  return (
    <S.CartItemWrapper
      as={motion.div}
      animate={{
        height: 'auto',
        opacity: 1,
      }}
      exit={{
        height: 0,
        opacity: 0,
      }}
    >
      <S.CartItemContainer tabIndex={0}>
        <S.InlineContainer>
          <S.CartItemLabel>
            <Button
              size='xs'
              variant='light'
              isIcon
              onClick={(e) => {
                e.stopPropagation();
                if (loading) return;
                dispatch(deleteCartItem(item.id));
              }}
            >
              <Trash2 fontSize={16} />
            </Button>
            {item.name}
          </S.CartItemLabel>
          <AnimatePresence>
            <S.CartItemPrice>
              {loading ? (
                <LoadingSpinner width={20} />
              ) : (
                <>
                  {item.cost_net === 0
                    ? 'FREE'
                    : `${currency}${(item.cost_net / 100).toFixed(2)}`}
                  {item.cost_net !== 0 &&
                    item.is_recurring &&
                    item.type.name !== 'Domain Name' &&
                    '/pm'}
                </>
              )}
            </S.CartItemPrice>
          </AnimatePresence>
        </S.InlineContainer>
      </S.CartItemContainer>
    </S.CartItemWrapper>
  );
};

const Help = ({ tip }: { tip: string }) => {
  if (!tip) return;
  return (
    <Popover tip={tip} delay={100}>
      <S.Help>
        <CircleHelp />
      </S.Help>
    </Popover>
  );
};

export const AdvancedCartItem = ({ item }: ICartItemProps) => {
  const dispatch = useTypedDispatch();
  const currency = useCurrency();

  const { loading } = item;

  const isDomain = item.type.name === 'Domain Name';
  const isDomainTransfer = isDomain && item.type.action === 'transfer';
  const isDomainBackorder = isDomain && item.type.action === 'backorder';
  const isPlanChange = item.type.name === 'VoIP Plan Change';
  const plans = useTypedSelector((state) => state.store.plan_prices?.plans);

  let descriptions = [];

  const itemName = item.name.replace(/(\d+) x /, '');

  let tip: any = null;

  switch (item.type.name) {
    case 'VoIP Plan Change':
      break;
    case 'Domain Name':
      if (isDomainBackorder) {
        tip =
          'Backorder Fee + Domain Registration Fee charged upon Backorder success.';
        descriptions.push(
          `Domain Name: ${itemName}`,
          `Estimated Registration Price: ${currency}${(item.cost_recurring / 100).toFixed(2)}`,
          `Backorder Service Fee: ${currency}${(item.cost_service / 100).toFixed(2)}`
        );
      } else if (isDomainTransfer) {
        descriptions.push(`Domain Name: ${itemName}`, 'Term: 1 year');
      } else {
        descriptions.push(
          `Domain Name: ${itemName}`,
          `Renews: ${moment().add(item.period, 'years').format('Do MMM YYYY')}`
        );
      }
      break;
    case 'Number':
      descriptions.push(
        `Number: ${itemName}`,
        `Price: ${item.cost_net > 0 ? `${currency}${(item.cost_net / 100).toFixed(3).slice(0, -1)} / per month` : 'Included in your monthly plan'}`
      );
      break;
    case 'Number Transfer':
      tip = 'One-off Number Transfer porting fee.';
      descriptions.push(
        `Number: ${itemName}`,
        `Price: ${item.cost_recurring > 0 ? `${currency}${(item.cost_recurring / 100).toFixed(3).slice(0, -1)} per month upon successful transfer` : 'Included in your monthly plan upon successful transfer'}`
      );
      break;
    case 'PaYG Call Credit':
      descriptions.push(`Price: one-off payment`);
      break;
    case 'User':
      descriptions.push(
        `Plan Price: ${item.cost_net > 0 ? `${currency}${(item.cost_net / 100 / item.period).toFixed(3).slice(0, -1)} per user / mth` : 'Included in your monthly plan'}`,
        `Renews: ${moment().add(1, 'months').format('Do MMM YYYY')}`
      );
      break;
    default:
      descriptions.push(itemName);
  }

  return (
    <S.CartItemWrapper
      as={motion.div}
      animate={{
        height: 'auto',
        opacity: 1,
      }}
      exit={{
        height: 0,
        opacity: 0,
      }}
      $advanced
    >
      <S.CartItemContainer tabIndex={0}>
        <S.ItemType>
          <Button
            size='xs'
            variant='light'
            isIcon
            onClick={(e) => {
              e.stopPropagation();
              if (loading) return;
              dispatch(deleteCartItem(item.id));
            }}
          >
            <Trash2 fontSize={16} />
          </Button>
          {isDomainTransfer
            ? 'Domain Name Transfer'
            : isDomainBackorder
              ? 'Domain Name Backorder'
              : item.type.name === 'PaYG Call Credit'
                ? 'Call Credit'
                : item.type.name === 'Number'
                  ? 'Phone Number'
                  : item.type.name === 'User'
                    ? `${plans.find((p) => p.id === item.type.sub_type_id + 1)?.name} Plan User`
                    : item.type.name}
          <Help tip={tip} />
          {item.type.name === 'User' && <VoipUserCount item={item} />}
          {item.type.name === 'Domain Name' &&
            !isDomainTransfer &&
            !isDomainBackorder &&
            item.cost_net !== item.cost_recurring && (
              <S.CartItemPrice
                style={{
                  textDecoration: 'line-through',
                  opacity: 0.4,
                  fontWeight: 500,
                }}
              >
                {currency}
                {(item.cost_recurring / 100).toFixed(2)}
              </S.CartItemPrice>
            )}
          <S.CartItemPrice>
            {loading ? (
              <LoadingSpinner width={20} />
            ) : (
              <>
                {item.cost_net === 0 && !isDomainBackorder
                  ? 'FREE'
                  : `${currency}${(item.cost_net / 100).toFixed(2)}`}
              </>
            )}
          </S.CartItemPrice>
        </S.ItemType>
        <Extras item={item} />
        <S.InlineContainer>
          {descriptions.map((d, i) => (
            <S.CartItemLabel key={i}>{d}</S.CartItemLabel>
          ))}
          {item.type.name === 'Domain Name' &&
            !isDomainBackorder &&
            item.cost_net !== item.cost_recurring &&
            item.cost_net &&
            item.cost_recurring && (
              <S.CartItemLabel>
                <span>
                  Promo:{' '}
                  {Math.round(
                    ((item.cost_recurring - item.cost_net) /
                      item.cost_recurring) *
                      100
                  )}
                  % Off
                </span>
              </S.CartItemLabel>
            )}
        </S.InlineContainer>
      </S.CartItemContainer>
    </S.CartItemWrapper>
  );
};

const CartDropdown = ({ footer, advanced }: ICartDropdownProps) => {
  const router = useRouter();
  const currency = useCurrency();
  const isGBP = currency === '£';

  const { cart, user } = useTypedSelector((state) => state.user);
  const incVat = isGBP && cart.cost_gross > 0;

  if (user.loading) {
    return <LoadingSpinner width={48} />;
  }

  return (
    <>
      <S.CartDropdownContainer>
        <S.CartItemsWrapper $advanced={advanced}>
          <AnimatePresence mode='popLayout'>
            {[...cart.items]
              .sort((a, b) => {
                if (a.type.name < b.type.name) {
                  return -1;
                }
                return 0;
              })
              .map((item) =>
                advanced ? (
                  <AdvancedCartItem item={item} key={item.id} />
                ) : (
                  <CartItem item={item} key={item.id} />
                )
              )}
          </AnimatePresence>
        </S.CartItemsWrapper>
        <S.CartFooter>
          {incVat && advanced && (
            <>
              <S.TotalContainer>
                <S.TotalLabel>Total ex VAT</S.TotalLabel>
                <S.TotalValue>
                  <span>{currency}</span>
                  {(cart.cost_net / 100).toFixed(2)}
                </S.TotalValue>
              </S.TotalContainer>
              <S.TotalContainer>
                <S.TotalLabel>VAT (20%)</S.TotalLabel>
                <S.TotalValue>
                  <span>{currency}</span>
                  {((cart.cost_gross - cart.cost_net) / 100).toFixed(2)}
                </S.TotalValue>
              </S.TotalContainer>
            </>
          )}
          <S.TotalContainer>
            <S.TotalLabel>
              Total {incVat && advanced && `(${cart.items.length} items)`}
            </S.TotalLabel>
            <S.TotalValue>
              <span>{currency}</span>
              {(cart.cost_gross / 100).toFixed(2)}
              {incVat && !advanced && (
                <P as='span' $small>
                  {' '}
                  (Inc. VAT)
                </P>
              )}
            </S.TotalValue>
          </S.TotalContainer>
          {footer ? (
            footer
          ) : (
            <S.FooterButton>
              <Button
                size='md'
                onClick={() => {
                  router.push('/shopping-cart');
                }}
              >
                Checkout
              </Button>
            </S.FooterButton>
          )}
        </S.CartFooter>
      </S.CartDropdownContainer>
    </>
  );
};

export default CartDropdown;
